import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../axios-orders";
import {
  PurchaseOrderResponse,
  RejectOcRequest,
  UpdateOcDraftRequest,
} from "../types";
import { queryKeys } from "./queryKeys";

/** True when the error is the backend's 409 (the OC is no longer in the state the action needs). */
export const isOcConflict = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status ===
  409;

/** True when the backend answered 403 (e.g. the user is not the OC's designated approver). */
export const isOcForbidden = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status ===
  403;

/** The `ErrorResponse.message` the backend sends when an OC's currency has no approval rule. */
export const OC_NO_APPROVAL_RULE = "oc.send.noApprovalRule";

/**
 * True for the send-block 409: the OC's currency has no approval rule and auto-approval is off.
 * Keyed on the message string (the error body has no `code` field) AND the 409 status, so any
 * other conflict keeps its generic handling.
 */
export const isNoApprovalRule = (error: unknown): boolean => {
  const response = (
    error as {
      response?: { status?: number; data?: { message?: string } };
    } | null
  )?.response;
  return (
    response?.status === 409 && response.data?.message === OC_NO_APPROVAL_RULE
  );
};

/**
 * Every OC action changes the OC's status/terms/timeline AND its row in the list, so each one
 * refreshes BOTH the detail (`[PURCHASE_ORDER, id]`) and the list (`[PURCHASE_ORDERS]`). It runs on
 * settle (not only on success) so a 409 — the OC moved on without this page knowing — also pulls
 * the real state, which hides the now-invalid action button.
 */
export const useInvalidateOc = () => {
  const queryClient = useQueryClient();
  return (id: string) => {
    queryClient.invalidateQueries({ queryKey: [queryKeys.PURCHASE_ORDER, id] });
    queryClient.invalidateQueries({ queryKey: [queryKeys.PURCHASE_ORDERS] });
    // An OC action can change a status, so the summary tiles must refetch too.
    queryClient.invalidateQueries({
      queryKey: [queryKeys.PURCHASE_ORDER_COUNTS],
    });
  };
};

export interface UpdateOcDraftVariables {
  id: string;
  terms: UpdateOcDraftRequest;
}

/** Edits a DRAFT OC's terms (`PUT`, REPLACE semantics — callers must send all five fields). */
export const useUpdateOcDraft = () => {
  const invalidate = useInvalidateOc();
  return useMutation<PurchaseOrderResponse, Error, UpdateOcDraftVariables>({
    mutationFn: async ({ id, terms }) => {
      const { data } = await axios.put(`/purchase-orders/${id}`, terms);
      return data;
    },
    onSettled: (_data, _error, { id }) => invalidate(id),
  });
};

const useOcPost = (
  action: "send" | "resend" | "remind-approver" | "approve"
) => {
  const invalidate = useInvalidateOc();
  return useMutation<PurchaseOrderResponse, Error, string>({
    mutationFn: async (id) => {
      const { data } = await axios.post(`/purchase-orders/${id}/${action}`);
      return data;
    },
    onSettled: (_data, _error, id) => invalidate(id),
  });
};

/** DRAFT -> PENDING_APPROVAL or SENT (the backend re-resolves the approval requirement). */
export const useSendOc = () => useOcPost("send");

/** SENT -> RESENT. */
export const useResendOc = () => useOcPost("resend");

/** Nudges the approver of a PENDING_APPROVAL OC. */
export const useRemindApprover = () => useOcPost("remind-approver");

/** PENDING_APPROVAL -> SENT. 403 = not the designated approver; 409 = no longer pending. */
export const useApproveOc = () => useOcPost("approve");

export interface CancelOcVariables {
  id: string;
  reason: string;
}

/**
 * Anular: any non-terminal OC -> CANCELLED, freeing its award. The reason is required (the backend
 * answers 400 for a blank one); 409 = already DELIVERED/CANCELLED.
 */
export const useCancelOc = () => {
  const invalidate = useInvalidateOc();
  return useMutation<PurchaseOrderResponse, Error, CancelOcVariables>({
    mutationFn: async ({ id, reason }) => {
      const { data } = await axios.post(`/purchase-orders/${id}/cancel`, {
        reason,
      });
      return data;
    },
    onSettled: (_data, _error, { id }) => invalidate(id),
  });
};

export interface RejectOcVariables extends RejectOcRequest {
  id: string;
}

/** PENDING_APPROVAL -> DRAFT. The reason is required (the backend rejects a blank one). */
export const useRejectOc = () => {
  const invalidate = useInvalidateOc();
  return useMutation<PurchaseOrderResponse, Error, RejectOcVariables>({
    mutationFn: async ({ id, reason, detail }) => {
      const body: RejectOcRequest = { reason };
      if (detail) body.detail = detail;
      const { data } = await axios.post(`/purchase-orders/${id}/reject`, body);
      return data;
    },
    onSettled: (_data, _error, { id }) => invalidate(id),
  });
};

/**
 * The OC's rendered PDF (`GET /purchase-orders/{id}/pdf`, `application/pdf` bytes) as a Blob.
 * `enabled` defers the fetch until the Documento tab is opened. Nested under the detail key, so
 * every OC action's invalidation also refreshes the document (it embeds the status/terms).
 */
export const useOcPdf = (id: string, enabled: boolean) =>
  useQuery<Blob>({
    queryKey: [queryKeys.PURCHASE_ORDER, id, "pdf"],
    enabled,
    // A refetch yields a new Blob -> new object URL -> the iframe reloads, losing scroll/zoom, and a
    // failed background refetch would blank the loaded viewer. Fetch once; explicit invalidation (on
    // OC actions) still refreshes it. Mirrors useOcToken.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
    queryFn: async () => {
      const { data } = await axios.get(`/purchase-orders/${id}/pdf`, {
        responseType: "blob",
      });
      return data as Blob;
    },
  });
